import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, MessageCircle, Ruler } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { MaskedLines, FadeUp } from "../components/motion";
import SEO from "../components/SEO";
import { PRODUCTS, INSTAGRAM_URL, WHATSAPP_URL, IMAGES } from "../data/products";

const Collection = () => {
  const { t } = useLanguage();
  const [filter, setFilter] = useState("all");
  const filters = ["all", "custom", "art", "solid"];
  const visible = filter === "all" ? PRODUCTS : PRODUCTS.filter((p) => p.category === filter);

  return (
    <>
      <SEO page="collection" />
      <section className="mx-auto max-w-[1400px] px-6 pt-40 lg:px-12 lg:pt-48" data-testid="collection-header">
        <p className="overline-label text-rouge">{t.collection.overline}</p>
        <h1 className="mt-8 font-display text-5xl font-bold leading-[1.02] tracking-tighter sm:text-6xl lg:text-7xl">
          <MaskedLines
            inView
            lines={[
              <span>{t.collection.title}</span>,
              <span className="italic font-medium text-rouge">{t.collection.titleItalic}</span>,
            ]}
          />
        </h1>
        <FadeUp delay={0.3} className="mt-8 flex flex-wrap items-end justify-between gap-8">
          <p className="max-w-xl text-base leading-relaxed text-stone2 md:text-lg">{t.collection.sub}</p>
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filters">
            {filters.map((fkey) => (
              <button
                key={fkey}
                role="tab"
                aria-selected={filter === fkey}
                onClick={() => setFilter(fkey)}
                data-testid={`filter-${fkey}`}
                className={`border px-5 py-2.5 text-xs font-bold uppercase tracking-[0.15em] transition-colors duration-300 ${
                  filter === fkey ? "border-rouge bg-rouge text-cream" : "border-ink/20 text-ink hover:border-ink"
                }`}
              >
                {t.collection.filters[fkey]}
              </button>
            ))}
          </div>
        </FadeUp>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 py-20 lg:px-12" data-testid="collection-grid">
        <motion.div layout className="grid gap-x-8 gap-y-16 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {visible.map((p, i) => (
              <motion.article
                layout
                key={p.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.07, ease: [0.16, 1, 0.3, 1] }}
                className={`group ${i % 3 === 1 ? "lg:translate-y-10" : ""}`}
                data-testid={`product-card-${p.id}`}
              >
                <div className="img-frame relative aspect-[4/5]">
                  <img
                    src={p.img}
                    alt={`Press on nagels ${p.name} — handgemaakt door On It Nails Herzele`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-x-0 bottom-0 flex translate-y-full gap-px transition-transform duration-500 ease-out group-hover:translate-y-0">
                    <a
                      href={INSTAGRAM_URL}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`product-dm-${p.id}`}
                      className="flex flex-1 items-center justify-center gap-2 bg-ink/90 py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] text-cream backdrop-blur transition-colors duration-300 hover:bg-rouge"
                    >
                      <Instagram size={14} /> {t.collection.order}
                    </a>
                    <a
                      href={WHATSAPP_URL}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`product-whatsapp-${p.id}`}
                      className="flex flex-1 items-center justify-center gap-2 bg-ink/90 py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] text-cream backdrop-blur transition-colors duration-300 hover:bg-rouge"
                    >
                      <MessageCircle size={14} /> {t.collection.orderWhatsapp}
                    </a>
                  </div>
                </div>
                <div className="mt-5 flex items-baseline justify-between gap-4">
                  <h2 className="font-display text-xl font-bold leading-tight">{p.name}</h2>
                  <p className="shrink-0 font-display text-lg font-bold text-rouge">
                    {p.from && <span className="mr-1 font-sans text-xs font-medium text-stone2">{t.collection.from}</span>}
                    €{p.price},00
                  </p>
                </div>
                <p className="mt-1 text-xs uppercase tracking-[0.2em] text-stone2">{t.collection.filters[p.category]}</p>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      <section className="border-t border-ink/10 bg-sand" data-testid="collection-fit-note">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-10 px-6 py-16 lg:px-12">
          <div className="img-frame h-24 w-24 shrink-0">
            <img src={IMAGES.sizingKit} alt="Sizing kit On It Nails — press-on nagels opmeten" loading="lazy" className="h-full w-full object-cover" />
          </div>
          <div className="flex-1">
            <p className="inline-flex items-center gap-2 font-display text-xl font-bold">
              <Ruler size={18} className="text-rouge" /> {t.collection.sizingKit}
            </p>
            <p className="mt-2 text-sm text-stone2">
              {t.collection.fitNote}
              <Link to="/hoe-het-werkt" className="link-underline font-semibold text-ink" data-testid="fit-note-link">
                {t.collection.fitLink}
              </Link>
              .
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default Collection;
